import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';

export interface ErrorBoundaryProps {
  /** Child components to wrap */
  children: React.ReactNode;
  /** Custom fallback UI to display on error */
  fallback?: React.ReactNode;
  /** Callback fired when an error is caught */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Callback fired when reset/retry is triggered */
  onReset?: () => void;
  /** Additional CSS classes for the error container */
  className?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

interface DefaultErrorFallbackProps {
  error: Error | null;
  onReset: () => void;
  onReload: () => void;
  className?: string | undefined;
}

/**
 * Default error fallback UI component with i18n support.
 * This is a functional component so it can use hooks.
 */
function DefaultErrorFallback({ error, onReset, onReload, className }: DefaultErrorFallbackProps) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        'flex min-h-[200px] flex-col items-center justify-center gap-4 p-8 text-center',
        'glass-card rounded-lg',
        className
      )}
      role="alert"
      aria-live="assertive"
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-error/10">
        <AlertTriangle className="h-8 w-8 text-error" aria-hidden="true" />
      </div>
      <div className="space-y-2">
        <h2 className="text-xl font-semibold text-foreground">
          {t('components.errorBoundary.title')}
        </h2>
        <p className="max-w-md text-sm text-foreground-muted">
          {t('components.errorBoundary.description')}
        </p>
        {import.meta.env.DEV && error && (
          <p className="mt-2 max-w-md font-mono text-xs text-error">{error.message}</p>
        )}
      </div>
      <div className="flex gap-3">
        <Button variant="outline" onClick={onReset}>
          {t('components.errorBoundary.tryAgain')}
        </Button>
        <Button variant="default" onClick={onReload}>
          {t('components.errorBoundary.reloadPage')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Error boundary component that catches React errors gracefully.
 *
 * Displays a user-friendly error message with cyberpunk styling and retry functionality.
 * Logs errors to console for debugging.
 * If no custom fallback is provided, shows a default error UI with i18n support.
 *
 * @example
 * // Basic usage
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * // With error callback and custom reset
 * <ErrorBoundary
 *   onError={(error) => logError(error)}
 *   onReset={() => clearState()}
 * >
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * // With custom fallback
 * <ErrorBoundary fallback={<div>Custom error message</div>}>
 *   <MyComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const { onError } = this.props;
    if (onError) {
      onError(error, errorInfo);
    }
    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = (): void => {
    const { onReset } = this.props;
    if (onReset) {
      onReset();
    }
    this.setState({ hasError: false, error: null });
  };

  handleReload = (): void => {
    window.location.reload();
  };

  override render(): React.ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback, className } = this.props;

    if (hasError) {
      // If custom fallback is provided, use it
      if (fallback) {
        return fallback;
      }

      // Default error UI with i18n support
      return (
        <DefaultErrorFallback
          error={error}
          onReset={this.handleReset}
          onReload={this.handleReload}
          className={className}
        />
      );
    }

    return children;
  }
}

export default ErrorBoundary;
